// Hand-mirrored catalog contracts (AL1). The console never had a catalog surface,
// so these are NOT in @csa/web-foundation's api/contracts.ts — and they are
// catalog-namespace-specific (AL2/AL3 are different namespaces), so they live HERE
// in apps/admin, not in the shared foundation (AL1 prompt §"Typed-client note").
//
// Mirrored by hand from the API — the source of record is
//   apps/api/src/modules/catalog/catalog.controller.ts (+ .service.ts)
//   packages/validation/src/catalog.ts  (curateCarrierQualitySchema)
//   packages/db/src/schema/catalog.ts    (carrier_quality_snapshot columns)
// kept aligned with the API. Offline OpenAPI generation is still blocked (foundation
// follow-up #1); on any ambiguous field, flag the API team — do NOT guess.

/** The six CURATED quality metrics. Each is a NON-NEGATIVE DECIMAL STRING matching
 *  the API's `qualityMetric` (`^\d{1,8}(\.\d{1,4})?$`) — sent as a string, never a
 *  JS number. At least one must be present (the curate `.refine`). */
export const QUALITY_METRIC_KEYS = [
  "claimsPaymentSpeedDays",
  "claimsAcceptanceRatio",
  "complaintRatio",
  "serviceScore",
  "solvencyRatio",
  "networkStrength",
] as const;
export type QualityMetricKey = (typeof QUALITY_METRIC_KEYS)[number];

/** The client-side mirror of the API's `qualityMetric` regex. The server is the
 *  authority (it returns VALIDATION_FAILED on a miss); this is UX pre-validation. */
export const QUALITY_METRIC_PATTERN = /^\d{1,8}(\.\d{1,4})?$/;

/** A light mirror of `z.string().datetime({ offset: true })` for as-of pre-validation
 *  (server is authoritative). Requires an explicit timezone offset (Z or ±HH:MM). */
export const ASOF_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** POST /v1/catalog/carriers/:code/quality body (curateCarrierQualitySchema,
 *  `.strict()`). `asOf` optional ISO-8601 WITH offset; omit → server now(). At least
 *  one metric required. */
export type CurateCarrierQualityInput = {
  asOf?: string;
} & Partial<Record<QualityMetricKey, string>>;

/** A carrier-quality snapshot row — GET/POST …/quality response. pg numerics
 *  serialize as decimal STRINGS (or null); timestamps as ISO strings. */
export interface CarrierQualitySnapshot {
  id: string;
  carrierId: string;
  asOf: string;
  source: string; // "CURATED" | "COLD_START" | "OWN_DATA"
  claimsPaymentSpeedDays: string | null;
  claimsAcceptanceRatio: string | null;
  complaintRatio: string | null;
  serviceScore: string | null;
  solvencyRatio: string | null;
  networkStrength: string | null;
  createdAt: string;
}

/** A1 — the composite the API now derives from the six metrics. `score` is NULL when
 *  the snapshot carries no usable metric: the ring renders "—", which is the truth. */
export interface QualityScoreFields {
  score: number | null;
  /** Per-metric normalised 0–100 value + the weight applied. Absent metrics omitted. */
  components: Partial<Record<QualityMetricKey, { value: number; weight: number }>>;
  metricsUsed: QualityMetricKey[];
  /** Σ raw weights of the PRESENT metrics — the confidence signal. 0.15 means 85% of
   *  the model is missing and the score should not be read as authoritative. */
  weightCoverage: number;
}

/** GET …/quality — the latest snapshot + its composite + the governed config that
 *  produced it (so a curator can tell WHICH weights the number was computed under). */
export type CarrierQualityView = CarrierQualitySnapshot &
  QualityScoreFields & { configVersion: number; configSource: string };

/** GET …/quality/history — the append-only series, newest first, each scored. */
export interface CarrierQualityHistory {
  data: (CarrierQualitySnapshot & QualityScoreFields)[];
  configVersion: number;
}

/** The fields of a carrier's current version the scorecard header renders (the API
 *  returns more; a read type need only declare what it consumes). */
export interface CarrierVersionView {
  legalName: string;
  tradeName: string;
  carrierType: string; // "CONVENTIONAL" | "KATILIM"
  operationalStatus: string; // "ACTIVE" | "SUSPENDED" | "WITHDRAWN"
}

/** GET /v1/catalog/carriers item / GET /v1/catalog/carriers/:code. */
export interface CarrierView {
  id: string;
  code: string;
  current: CarrierVersionView;
}

// ── Catalog MANAGEMENT (self-service carrier + version + line-authorization) ──
// Source of record (kept aligned by hand, offline OpenAPI gen still blocked):
//   apps/api/src/modules/catalog/catalog.controller.ts (+ .service.ts)
//   packages/validation/src/catalog.ts  (createCarrierSchema, carrierVersionSchema,
//                                         addBranchAuthorizationSchema)
//   packages/db/src/schema/catalog.ts    (carrier_version, carrier_branch_authorization,
//                                          line columns; CARRIER_TYPES, OPERATIONAL_STATUSES)

/** The carrier-type vocabulary — the API enum (packages/db CARRIER_TYPES). */
export const CARRIER_TYPES = ["CONVENTIONAL", "KATILIM"] as const;
export type CarrierType = (typeof CARRIER_TYPES)[number];

/** The operational-status vocabulary — the API enum (packages/db OPERATIONAL_STATUSES). */
export const OPERATIONAL_STATUSES = ["ACTIVE", "SUSPENDED", "WITHDRAWN"] as const;
export type OperationalStatus = (typeof OPERATIONAL_STATUSES)[number];

/** Client mirror of the API's `code` regex (lowercase-kebab; packages/validation).
 *  Server is authoritative (returns VALIDATION_FAILED on a miss); this is UX. */
export const CODE_PATTERN = /^[a-z][a-z0-9-]{1,63}$/;

/** POST /v1/catalog/carriers body (createCarrierSchema). A version carries the FULL
 *  attribute set (ADR 0004 §2 — append-only). `operationalStatus` defaults ACTIVE
 *  server-side; `groupCode` optional. */
export interface CreateCarrierInput {
  code: string; // lowercase-kebab, immutable identity
  legalName: string;
  tradeName: string;
  carrierType: CarrierType;
  groupCode?: string;
  operationalStatus?: OperationalStatus;
}

/** POST /v1/catalog/carriers/:code/versions body (carrierVersionSchema). The full
 *  next-state (never a patch): the server closes the open version and opens this one. */
export type CarrierVersionInput = Omit<CreateCarrierInput, "code">;

/** A carrier_version row — the append-only timeline (…/versions history + the write
 *  response `current`). `effectiveTo === null` = the open/current version. Timestamps
 *  ISO strings. */
export interface CarrierVersionRow {
  id: string;
  carrierId: string;
  legalName: string;
  tradeName: string;
  carrierType: CarrierType;
  groupId: string | null;
  operationalStatus: OperationalStatus;
  version: number;
  effectiveFrom: string;
  effectiveTo: string | null; // null = open/current
  status: string; // DRAFT | ACTIVE | RETIRED
  createdAt: string;
  updatedAt: string;
}

/** POST /v1/catalog/carriers/:code/authorizations body (addBranchAuthorizationSchema).
 *  `lineCode` from GET /v1/catalog/lines; `authorizedTo` optional ISO-8601 with offset. */
export interface AddBranchAuthorizationInput {
  lineCode: string;
  authorizedTo?: string;
}

/** A carrier_branch_authorization row — GET …/authorizations item. Effective-dated;
 *  `authorizedTo === null` = the open authorization. */
export interface CarrierBranchAuthorizationRow {
  id: string;
  carrierId: string;
  lineCode: string;
  authorizedFrom: string;
  authorizedTo: string | null; // null = open
  createdAt: string;
  updatedAt: string;
}

/** GET /v1/catalog/lines item (the branch/line picker; catalog:read @ all). Only the
 *  fields the picker renders are declared. */
export interface LineRow {
  code: string; // kasko, trafik, konut, …
  pillarCode: string;
  name: string;
  networked: boolean;
  insurableAssetType: string;
}

// ── Catalog step 2 — PRODUCTS · PACKAGES · COVERAGES · CONCEPTS ───────────────
// Source of record (kept aligned by hand, offline OpenAPI gen still blocked #1):
//   apps/api/src/modules/catalog/product-catalog.controller.ts (+ .service.ts)
//   packages/validation/src/catalog-product.ts  (create/version schemas, enums)
//   packages/db/src/schema/catalog.ts  (product_version, package_version, coverage_*)
// Same versioning contract as carriers (ADR 0004 §§4-5): identity + append-only,
// whole-state versions. A package version PINS its coverage-set at creation.
// Equivalence writes are FOUR-EYES (controller @RequireFourEyes) — NOT surfaced here
// (see the surface note); this file mirrors only the non-four-eyes writes/reads.

/** Client mirrors of the API's money / percent regexes (catalog-product.ts). Server is
 *  authoritative (VALIDATION_FAILED on a miss); these are UX pre-validation. Money and
 *  percent travel as DECIMAL STRINGS, never JS numbers (the platform-wide rule). */
export const MONEY_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;
export const PERCENT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

// ── Products ─────────────────────────────────────────────────────────────────

/** salesStatus vocabulary (createProductSchema / productVersionSchema). */
export const SALES_STATUSES = ["ACTIVE", "PAUSED", "DISCONTINUED"] as const;
export type SalesStatus = (typeof SALES_STATUSES)[number];

/** The whole-state attributes a product version carries (versions are states, not
 *  patches). `salesStatus` defaults ACTIVE server-side. */
export interface ProductVersionInput {
  name: string;
  targetSegment?: string;
  salesStatus: SalesStatus;
}

/** POST /v1/catalog/products body (createProductSchema). Identity (immutable code +
 *  carrier + line) plus version 1's attributes. catalog:write @ all, audited. */
export interface CreateProductInput extends ProductVersionInput {
  code: string; // lowercase-kebab, immutable identity
  carrierCode: string;
  lineCode: string;
}

/** A product_version row — the write response `current` / GET …/products/:code
 *  `current`. Only the fields a read consumes are declared. `effectiveTo === null`
 *  = the open/current version. */
export interface ProductVersionRow {
  id: string;
  name: string;
  targetSegment: string | null;
  salesStatus: SalesStatus;
  version: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** Identity + current version. Returned by GET /v1/catalog/products/:code (single)
 *  and as each row of GET /v1/catalog/products (Page envelope, A9). */
export interface ProductView {
  id: string;
  code: string;
  current: ProductVersionRow;
}

// ── Packages (the quotable unit; coverage-set frozen with the version) ────────

/** renewalGuarantee vocabulary (packageAttributes). */
export const RENEWAL_GUARANTEES = ["NONE", "CONDITIONAL", "LIFETIME"] as const;
export type RenewalGuarantee = (typeof RENEWAL_GUARANTEES)[number];

/** preexistingHandling vocabulary (packageAttributes). */
export const PREEXISTING_HANDLINGS = [
  "NOT_APPLICABLE",
  "EXCLUDED",
  "COVERED_AFTER_WAITING",
  "UNDERWRITTEN",
] as const;
export type PreexistingHandling = (typeof PREEXISTING_HANDLINGS)[number];

/** A pinned coverage's role within the package (packageAttributes.coverages[].role). */
export const COVERAGE_ROLES = ["CORE", "RIDER", "OPTIONAL"] as const;
export type CoverageRole = (typeof COVERAGE_ROLES)[number];

/** One entry of a package version's coverage-set. Optional per-package overrides are
 *  decimal strings. The set is resolved to the coverages' CURRENT versions at pin
 *  time (server-side) and then frozen. */
export interface PackageCoverageInput {
  coverageCode: string;
  role: CoverageRole;
  limitAmountOverride?: string;
  deductibleAmountOverride?: string;
  coPayPercentOverride?: string;
}

/** The whole-state attributes a package version carries. `renewalGuarantee` /
 *  `preexistingHandling` default server-side; `networkCode` is required when the line
 *  is networked, forbidden otherwise (server-enforced). `coverages` is min 1. */
export interface PackageVersionInput {
  name: string;
  renewalGuarantee: RenewalGuarantee;
  minEntryAge?: number;
  maxEntryAge?: number;
  preexistingHandling: PreexistingHandling;
  networkCode?: string;
  coverages: PackageCoverageInput[];
}

/** POST /v1/catalog/packages body (createPackageSchema). Identity (immutable code +
 *  product) plus version 1's attributes + pinned coverage-set. catalog:write @ all. */
export interface CreatePackageInput extends PackageVersionInput {
  code: string; // lowercase-kebab, immutable identity
  productCode: string;
}

/** GET /v1/catalog/packages?line= item — the advisor-dropdown feed: the distributable
 *  packages of a line, org-gated (the ONLY package LIST read). */
export interface DistributablePackage {
  packageCode: string;
  carrierTradeName: string;
  packageName: string;
  lineCode: string;
}

/** A package_version row — the write response `current` / GET …/packages/:code
 *  `current`. `effectiveTo === null` = the open/current version. */
export interface PackageVersionRow {
  id: string;
  name: string;
  renewalGuarantee: RenewalGuarantee;
  minEntryAge: number | null;
  maxEntryAge: number | null;
  preexistingHandling: PreexistingHandling;
  version: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** A pinned, frozen coverage of the package's current version (GET …/packages/:code
 *  `coverages[]`). The pinned coverage VERSION is what was resolved at pin time. */
export interface PinnedCoverage {
  coverageCode: string;
  coverageVersion: number;
  role: CoverageRole;
  name: string;
  limitAmount: string | null;
  limitAmountOverride: string | null;
}

/** GET /v1/catalog/packages/:code — identity + current version + pinned coverage-set. */
export interface PackageView {
  id: string;
  code: string;
  current: PackageVersionRow;
  coverages: PinnedCoverage[];
}

// ── Concepts (canonical, per line) ────────────────────────────────────────────

/** POST /v1/catalog/concepts body (createConceptSchema). catalog:write @ all. */
export interface CreateConceptInput {
  code: string; // lowercase-kebab, immutable identity
  lineCode: string;
  name: string;
  definition: string;
}

/** GET /v1/catalog/lines/:line/concepts item — the canonical concepts of a line. */
export interface ConceptRow {
  code: string;
  name: string;
  definition: string;
}

// ── Coverages (carrier attribute block; NOT four-eyes — equivalence IS) ───────

/** One sub-limit of a coverage version (coverageAttributes.subLimits[]). Amount is a
 *  decimal string. */
export interface SubLimitInput {
  name: string;
  amount: string;
}

/** The whole-state attributes a coverage version carries (coverageAttributes). All
 *  money/percent fields are decimal strings; server refuses in-place edits. */
export interface CoverageVersionInput {
  name: string;
  wordingRef?: string;
  limitAmount?: string;
  limitUnlimited: boolean;
  deductibleAmount?: string;
  deductiblePercent?: string;
  coPayPercent?: string;
  waitingPeriodDays?: number;
  geographicScope?: string;
  exclusions?: string[];
  subLimits: SubLimitInput[];
}

/** POST /v1/catalog/coverages body (createCoverageSchema). Identity (immutable code +
 *  carrier + line) plus version 1's attributes. catalog:write @ all, audited. */
export interface CreateCoverageInput extends CoverageVersionInput {
  code: string; // lowercase-kebab, immutable identity
  carrierCode: string;
  lineCode: string;
}

/** A coverage_version row — GET …/coverages/:code `current`. */
export interface CoverageVersionRow {
  id: string;
  name: string;
  limitAmount: string | null;
  limitUnlimited: boolean;
  deductibleAmount: string | null;
  deductiblePercent: string | null;
  coPayPercent: string | null;
  waitingPeriodDays: number | null;
  geographicScope: string | null;
  version: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** GET /v1/catalog/coverages/:code — identity + current version + its sub-limits
 *  (there is NO coverage LIST endpoint; the surface looks a coverage up by code). */
export interface CoverageView {
  id: string;
  code: string;
  current: CoverageVersionRow;
  subLimits: SubLimitInput[];
}
